import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import Global from '../../Services/Common/Global';
import LogoImg from '../../Assets/Images/LogoWidth.svg';
import { MOK_FINDPW_RESULT_KEY } from './IdentityMokCallback';

/** MOBILEOK.process 3번째 인자 — 팝업 완료 시 호출할 전역 콜백명 (WB + 콜백 = 새창) */
const MOK_FINDPW_CALLBACK_NAME = 'MokFindPwCallback';

function FindPw({
  open,
  onClose,
  findPwInputs,
  setFindPwInputs,
  SaveLogic4,
  loading,
}) {
  const [mokLoading, setMokLoading] = useState(false);

  const ApplyMokResultToInputs = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object') return;

      const name = payload.userName ?? '';
      const tel = (payload.userPhone ?? '').replace(/[^0-9]/g, '');

      setFindPwInputs((prev) => ({
        ...prev,
        identityConfirm: true,
        identityErrMsg: '',
        userNm: name,
        userTel: tel,
        userId: tel,
      }));
    },
    [setFindPwInputs]
  );

  useEffect(() => {
    if (!open) return;
    const src = process.env.REACT_APP_MOBILE_OK;
    if (!src || document.querySelector(`script[src="${src}"]`)) return;
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }, [open]);

  useEffect(() => {
    return () => {
      delete window[MOK_FINDPW_CALLBACK_NAME];
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const stored = sessionStorage.getItem(MOK_FINDPW_RESULT_KEY);
    if (!stored) return;
    try {
      const result = JSON.parse(stored);
      ApplyMokResultToInputs(result);
    } catch (_) {
      // ignore
    } finally {
      sessionStorage.removeItem(MOK_FINDPW_RESULT_KEY);
    }
  }, [open, ApplyMokResultToInputs]);

  const HandlePrimaryAction = async () => {
    if (!findPwInputs.identityConfirm) {
      setMokLoading(true);
      setFindPwInputs((prev) => ({
        ...prev,
        identityErrMsg: '',
      }));

      try {
        /**
         * WB callback 방식에서는 returnUrl을 서버에 전달하지 않는다.
         * returnUrl을 전달하면 서버가 Redirect 응답을 반환하고,
         * client_process.js의 XHR이 리다이렉트를 따라가면서
         * 포트가 다른 cross-origin 간 CORS 오류가 발생한다.
         * returnUrl 없이 요청하면 서버(ResultController)가 JSON을 직접 응답하고,
         * SDK가 그 응답을 콜백 함수에 전달한다.
         */
        const mokRequestUrl = `${Global.base.useUrl}${Global.urls.Account.mokRequestPageUrl}`;

        if (window.MOBILEOK && typeof window.MOBILEOK.process === 'function') {
          // WB + 콜백함수명: 팝업(새창), MB + "": 모바일 전체화면/페이지이동
          window[MOK_FINDPW_CALLBACK_NAME] = (rawResult) => {
            setMokLoading(false);
            try {
              let parsed = rawResult;
              if (typeof rawResult === 'string') {
                parsed = JSON.parse(rawResult);
              }
              if (parsed?.resultCode && String(parsed.resultCode) !== '2000') {
                throw new Error(
                  parsed?.resultMsg || '본인확인에 실패했습니다.'
                );
              }
              ApplyMokResultToInputs(parsed);
            } catch (e) {
              setFindPwInputs((prev) => ({
                ...prev,
                identityErrMsg:
                  e?.message ||
                  '본인인증 처리에 실패했습니다. 다시 시도해 주세요.',
              }));
            }
          };

          window.MOBILEOK.process(
            mokRequestUrl,
            'WB',
            MOK_FINDPW_CALLBACK_NAME
          );
          setMokLoading(false);
          return;
        }

        throw new Error('본인확인 SDK가 로드되지 않았습니다.');
      } catch (e) {
        setMokLoading(false);
        setFindPwInputs((prev) => ({
          ...prev,
          identityErrMsg:
            e?.message || '본인인증 요청에 실패했습니다. 다시 시도해 주세요.',
        }));
      }
      return;
    }
    await SaveLogic4();
  };

  if (!open) return null;

  const primaryDisabled =
    mokLoading ||
    loading ||
    (findPwInputs.identityConfirm && !findPwInputs.userTel);

  const primaryLabel = findPwInputs.identityConfirm
    ? loading
      ? '발급 중...'
      : '임시 비밀번호 발급'
    : mokLoading
      ? '인증 요청 중...'
      : '전화번호 본인인증';

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-labelledby="findpw-title"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2.5,
      }}
    >
      <Box
        component="button"
        type="button"
        aria-label="닫기"
        onClick={onClose}
        sx={{
          position: 'absolute',
          inset: 0,
          border: 'none',
          bgcolor: 'rgba(0,0,0,0.4)',
          cursor: 'pointer',
          p: 0,
        }}
      />

      <Box
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: 340,
          borderRadius: '24px',
          bgcolor: '#fff',
          px: 3,
          pt: 5,
          pb: 3.5,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="닫기"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: '#9CA3AF',
            '&:hover': { color: '#4B5563' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        {findPwInputs.successful ? (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                mx: 'auto',
                mb: 2.5,
                width: 64,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: '2px solid #2EBD36',
                color: '#2EBD36',
              }}
            >
              <CheckIcon sx={{ fontSize: 32 }} />
            </Box>

            <Typography
              id="findpw-title"
              component="h2"
              sx={{
                mb: 3,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#2EBD36',
              }}
            >
              임시 비밀번호 발급 완료
            </Typography>

            <Box
              sx={{
                mb: 3,
                borderRadius: '16px',
                border: '1px solid rgba(46,189,54,0.6)',
                px: 2.5,
                py: 2,
                textAlign: 'left',
              }}
            >
              <Typography
                sx={{
                  mb: 2,
                  fontSize: '15px',
                  lineHeight: 1.65,
                  color: '#374151',
                }}
              >
                {findPwInputs.issueMessage || '임시 비밀번호가 발급되었습니다.'}
              </Typography>

              {findPwInputs.tempPassword ? (
                <Box component="dl" sx={{ m: 0, fontSize: '0.875rem' }}>
                  <Typography
                    component="dt"
                    sx={{ mb: 0.5, fontWeight: 500, color: '#6B7280' }}
                  >
                    임시 비밀번호
                  </Typography>
                  <Typography
                    component="dd"
                    sx={{
                      m: 0,
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      wordBreak: 'break-all',
                      color: '#111827',
                    }}
                  >
                    {findPwInputs.tempPassword}
                  </Typography>
                </Box>
              ) : null}

              <Typography
                sx={{
                  mt: 2,
                  fontSize: '0.875rem',
                  lineHeight: 1.65,
                  color: '#6B7280',
                }}
              >
                발급된 임시 비밀번호로 로그인한 뒤, 마이페이지에서 꼭 비밀번호
                변경을 진행해 주세요.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src={LogoImg}
                alt="로고"
                sx={{ height: 'auto', width: '72%', maxWidth: 220 }}
              />
            </Box>

            <Typography
              id="findpw-title"
              sx={{
                mb: 0.5,
                textAlign: 'center',
                fontSize: '15px',
                color: '#6B7280',
              }}
            >
              비밀번호를 잊어버리셨나요?
            </Typography>
            <Typography
              sx={{
                mb: 3,
                textAlign: 'center',
                fontSize: '0.875rem',
                lineHeight: 1.65,
                color: '#9CA3AF',
              }}
            >
              회원가입 시 작성하셨던 전화번호로
              <br />
              본인인증을 진행해 주세요.
            </Typography>

            {findPwInputs.identityErrMsg ? (
              <Typography
                sx={{
                  mb: 1.5,
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#DC2626',
                }}
              >
                {findPwInputs.identityErrMsg}
              </Typography>
            ) : null}

            {findPwInputs.identityConfirm ? (
              <Typography
                sx={{
                  mb: 1.5,
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#2EBD36',
                }}
              >
                본인인증이 완료되었습니다.
              </Typography>
            ) : null}

            <Button
              type="button"
              fullWidth
              disabled={primaryDisabled}
              onClick={HandlePrimaryAction}
              sx={{
                height: 52,
                borderRadius: '12px',
                bgcolor: '#2EBD36',
                fontSize: '1rem',
                fontWeight: 600,
                color: '#fff',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#25a82f',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: '#D1D5DB',
                  color: '#fff',
                },
              }}
            >
              {primaryLabel}
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export default FindPw;
